import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Activity, LayoutDashboard, MonitorDot, ScrollText, Tags, Users, Settings, LogOut } from "lucide-react";
import { toast } from "sonner";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", master: false },
  { to: "/devices", label: "Perangkat", icon: MonitorDot, testid: "nav-devices-link", master: false },
  { to: "/logs", label: "Log", icon: ScrollText, testid: "nav-logs-link", master: false },
  { to: "/users", label: "User", icon: Users, testid: "nav-users-link", master: true },
  { to: "/settings", label: "Setting", icon: Settings, testid: "nav-settings-link", master: false },
];

export default function Layout() {
  const { user, isMaster, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    toast.success("Berhasil keluar");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] grid-noise text-[#F9FAFB]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0B0F17]/80 border-b border-[#1F2937]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#3B82F6] to-[#06B6D4] flex items-center justify-center">
              <Activity className="text-white" size={18} />
            </div>
            <span className="font-display font-extrabold tracking-tight text-lg hidden sm:block">PanelPulse</span>
          </div>

          <nav className="flex items-center gap-1 flex-1 justify-center overflow-x-auto">
            {navItems.filter((n) => !n.master || isMaster).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive ? "bg-[#1F2937] text-white" : "text-[#9CA3AF] hover:text-white hover:bg-[#111827]"
                  }`
                }
              >
                <n.icon size={16} />
                <span className="hidden md:inline">{n.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold leading-none">{user?.username}</div>
              <Badge
                data-testid="role-badge"
                className={`mt-1 text-[10px] px-1.5 py-0 ${
                  isMaster ? "bg-[#3B82F6]/15 text-[#60A5FA] border-[#3B82F6]/30" : "bg-[#06B6D4]/15 text-[#22D3EE] border-[#06B6D4]/30"
                }`}
              >
                {isMaster ? "MASTER" : "MEMBER"}
              </Badge>
            </div>
            <Button
              data-testid="logout-button"
              onClick={doLogout}
              variant="ghost"
              size="icon"
              className="text-[#9CA3AF] hover:text-white hover:bg-[#1F2937]"
            >
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
